import type { PortfolioProject } from '@/types/portfolio';
import type { Testimonial, BlogPost, FAQItem } from '@/types/content';
import type { ServicePackage, CarePlan } from '@/types/services';

export const portfolioProjects: PortfolioProject[] = [
  {
    slug: 'baldie-con',
    name: 'Baldie Con',
    orgType: 'nonprofit',
    heroImage: '/images/projects/baldie-con/homepage.webp',
    tagline: 'Radically accessible national conference platform for a 501(c)(3) nonprofit.',
    impactStatement: 'Zero downtime during Vogue, CBS, and Jennifer Hudson Show features.',
    role: 'Lead Developer & Accessibility Engineer',
    timeline: '8 weeks',
    techStack: ['Next.js', 'TypeScript', 'WCAG 2.2 AA', '100 Accessibility'],
    complianceLevel: 'WCAG 2.2 AA',
    lighthousePerformance: 98,
    lighthouseAccessibility: 100,
    hook: "Baldie Con was being featured in Vogue and on The Jennifer Hudson Show. Their website couldn't keep up with the moment.",
    challenge: [
      "Accessibility barriers creating a 'digital wall' for their own community",
      "Traffic spikes from national press overwhelming the old platform",
      "Website didn't match their premium, nationally-recognized brand identity",
    ],
    solution: "We rebuilt the entire platform from the ground up with accessibility as the foundation, not an afterthought. The new site handles traffic spikes gracefully while maintaining a perfect Lighthouse accessibility score.",
    impactMetrics: [
      { metric: 'Accessibility', result: 'WCAG 2.2 AA tested' },
      { metric: 'Lighthouse Accessibility', result: '100/100' },
      { metric: 'Lighthouse Performance', result: '98+' },
      { metric: 'Vendor marketplace', result: '40+ partners' },
      { metric: 'Press resilience', result: 'Professional Grant-Ready Presence' },
      { metric: 'Conference growth', result: 'Local gathering → 6-city national tour' },
    ],
    /* Testimonial commented out
    testimonial: {
      quote: "Crystal rebuilt our site weeks before a major grant application deadline. The funder specifically mentioned our website's professionalism.",
      name: 'Sarah M.',
      title: 'Executive Director',
      org: 'Metro Atlanta Food Bank',
    },
    */
    gallery: {
      images: [
        { src: '/images/projects/baldie-con/homepage.webp', alt: 'Baldie Con homepage hero section' },
        { src: '/images/projects/baldie-con/devices.webp', alt: 'Baldie Con cross-device responsiveness' },
        { src: '/images/projects/baldie-con/speakers.webp', alt: 'Baldie Con speakers directory' },
        { src: '/images/projects/baldie-con/tickets.webp', alt: 'Baldie Con ticketing interface' },
        { src: '/images/projects/baldie-con/venues.webp', alt: 'Baldie Con national tour venues' },
      ],
      caption: 'All screenshots from live production site.',
    },
    relatedProjects: ['georgia-homeless-pets'],
    pressCallout: {
      quote: "Professional Grant-Ready Presence",
      media: ['Vogue', 'CBS', 'Jennifer Hudson Show']
    },
    solutions: [
      { title: 'Performance', description: 'Built on Next.js for high-performance static delivery.' },
      { title: 'Accessibility', description: 'WCAG 2.2 AA compliant ensuring every visitor can navigate the platform.' },
      { title: 'Vendor Network', description: 'Integrated marketplace for 40+ national partners to manage listings.' },
      { title: 'Scale', description: 'Architecture designed to support an 8-fold increase in conference capacity.' },
    ],
  },
  {
    slug: 'georgia-homeless-pets',
    name: 'Georgia Homeless Pets',
    orgType: 'nonprofit',
    heroImage: '/images/projects/georgia-homeless-pets/homepage.webp',
    tagline: "High-trust rescue platform for Georgia's premier foster-based animal rescue.",
    impactStatement: 'Streamlined 100% volunteer-led rescue operations with zero code maintenance.',
    role: 'Lead Developer & Accessibility Engineer',
    timeline: '6 weeks',
    techStack: ['React', 'TanStack Query', 'WCAG 2.1 AA', '100 Accessibility'],
    complianceLevel: 'WCAG 2.1 AA',
    lighthousePerformance: 95,
    lighthouseAccessibility: 100,
    hook: "Georgia Homeless Pets saves hundreds of animals from high-kill shelters every year, entirely without a building. Their website has to earn the trust a physical location would.",
    challenge: [
      'Without a physical facility, trust must be established entirely through digital presence',
      'Complex adoption and foster applications overwhelming 100% volunteer team',
      'Fragmented listings making it hard for adopters to find available animals',
    ],
    solution: "We designed a trust-first digital experience that replaces the need for a physical storefront, with automated application routing and a unified animal listing system.",
    impactMetrics: [
      { metric: 'Accessibility', result: 'WCAG 2.1 AA' },
      { metric: 'Lighthouse Accessibility', result: '100/100' },
      { metric: 'Lighthouse Performance', result: '95+' },
      { metric: 'Infrastructure cost', result: '$0: Vercel static hosting' },
      { metric: 'Application routing', result: 'Fully automated' },
      { metric: 'Post-launch overhead', result: 'Zero for volunteer team' },
    ],
    gallery: {
      images: [
        { src: '/images/projects/georgia-homeless-pets/homepage.webp', alt: 'Georgia Homeless Pets homepage' },
        { src: '/images/projects/georgia-homeless-pets/locations.webp', alt: 'Georgia Homeless Pets partner locations' },
        { src: '/images/projects/georgia-homeless-pets/adopt.webp', alt: 'Georgia Homeless Pets adoption flow' },
        { src: '/images/projects/georgia-homeless-pets/contact.webp', alt: 'Georgia Homeless Pets contact interface' },
      ],
      caption: 'All screenshots from live production site.',
    },
    beforeAfter: {
      before: { src: '/placeholder.svg', alt: 'Georgia Homeless Pets website before redesign' },
      after: { src: '/images/projects/georgia-homeless-pets/devices.webp', alt: 'Georgia Homeless Pets website after redesign' },
      caption: 'Screenshots sourced from the Wayback Machine and live production site.',
    },
    relatedProjects: ['baldie-con', 'village-at-cobblestone'],
    solutions: [
      { title: 'Trust-First Design', description: 'Visual language that establishes credibility for a foster-based rescue.' },
      { title: 'Automation', description: 'Direct application routing to volunteer coordinators based on animal type.' },
      { title: 'Performance', description: 'Optimized static delivery for fast loading on cellular networks.' },
      { title: 'Accessibility', description: 'Full WCAG 2.1 AA compliance for all adoption and foster forms.' },
    ],
  },
  {
    slug: 'village-at-cobblestone',
    name: 'The Village at Cobblestone',
    orgType: 'hoa',
    heroImage: '/images/projects/village-at-cobblestone/homepage.webp',
    tagline: 'Resident-maintained community hub for a 225-home Georgia neighborhood.',
    impactStatement: 'Eliminated manual new-neighbor PDF packets and board burnout.',
    role: 'Lead Developer',
    timeline: '5 weeks',
    techStack: ['React', 'Sanity CMS', 'WCAG 2.1 AA'],
    complianceLevel: 'WCAG 2.1 AA',
    lighthousePerformance: 95,
    lighthouseAccessibility: 100,
    hook: "225 households, 600+ residents, and a 30-year-old neighborhood running on paper packets, email chains, and a Facebook group. We built them a digital front porch.",
    challenge: [
      "Residents couldn't find basic answers without calling board members directly",
      'Board volunteers overwhelmed by repetitive questions about trash, rules, and vendors',
      'Vital neighborhood knowledge (trusted plumbers, fence specs) lost in email threads',
    ],
    solution: "We created a centralized community hub that puts every resource, vendor recommendation, and neighborhood rule at residents' fingertips, which eliminates the volunteer burden on board members.",
    impactMetrics: [
      { metric: 'Households served', result: '225 single-family homes' },
      { metric: 'Resources centralized', result: '20+ utility and vendor providers' },
      { metric: 'Accessibility', result: 'WCAG 2.1 AA' },
      { metric: 'Lighthouse Accessibility', result: '100/100' },
      { metric: 'Lighthouse Performance', result: '95+' },
      { metric: 'Infrastructure', result: '$0 via Vercel' },
      { metric: 'New neighbor onboarding', result: 'PDF packets eliminated' },
    ],
    /* Testimonial commented out
    testimonial: {
      quote: "We went from a Facebook group and email chains to a real community hub. Our residents actually use it.",
      name: 'David K.',
      title: 'HOA Board President',
      org: 'Village at Cobblestone',
    },
    */
    gallery: {
      images: [
        { src: '/images/projects/village-at-cobblestone/homepage.webp', alt: 'Village at Cobblestone community hub homepage' },
        { src: '/images/projects/village-at-cobblestone/homes.webp', alt: 'Village at Cobblestone homes directory' },
        { src: '/images/projects/village-at-cobblestone/about.webp', alt: 'Village at Cobblestone about section' },
        { src: '/images/projects/village-at-cobblestone/devices.webp', alt: 'Village at Cobblestone cross-device view' },
      ],
      caption: 'All screenshots from live production site.',
    },
    relatedProjects: ['georgia-homeless-pets'],
    solutions: [
      { title: 'Community Hub', description: 'A single destination for all neighborhood resources and announcements.' },
      { title: 'Resource Center', description: 'Centralized directory of utilities, rules, and trusted vendor lists.' },
      { title: 'Board Relief', description: 'Automated answers to common questions reducing volunteer burnout.' },
      { title: 'Mobile First', description: 'Responsive design for residents checking rules while on the go.' },
    ],
  },
  {
    slug: 'the-clock-avenue',
    name: 'The Clock Avenue',
    orgType: 'small-business',
    caseStudyPending: true,
    heroImage: '/images/projects/the-clock-avenue/the-clock-avenue.webp',
    tagline: 'Precision digital presence for a specialty clock and parts retailer.',
    impactStatement: 'Integrated Square commerce platform built for technical accuracy.',
    role: 'Digital Strategy & Design',
    timeline: '4 weeks',
    techStack: ['Square Online', 'E-commerce Strategy', 'Inventory Management'],
    complianceLevel: 'Standard',
    lighthousePerformance: 92,
    lighthouseAccessibility: 96,
    hook: "A specialty clock shop needing a streamlined digital storefront that handles complex parts inventory and premium clock sales.",
    challenge: ["Managing complex parts inventory", "Outdated legacy storefront", "Mobile checkout friction"],
    solution: "A custom-configured Square Online storefront optimized for technical parts discovery and a seamless customer checkout experience.",
    impactMetrics: [
      { metric: 'Performance', result: '90+' },
      { metric: 'Accessibility', result: '95+' }
    ],
    gallery: {
      images: [{ src: '/images/projects/the-clock-avenue/the-clock-avenue.webp', alt: 'The Clock Avenue homepage' }],
      caption: 'Live production site.'
    },
    relatedProjects: ['baldie-con']
  },
  {
    slug: 'grace-community-chapel',
    name: 'Grace Community Chapel',
    orgType: 'church',
    isMock: true,
    heroImage: '/images/projects/other/church-site.webp',
    tagline: 'Modern, welcoming digital threshold for a growing congregation.',
    impactStatement: 'Visual-first design focusing on visitor onboarding and giving.',
    role: 'Product Strategy & Design',
    timeline: 'Productized',
    techStack: ['Next.js', 'Tailwind CSS', 'WCAG 2.1 AA'],
    complianceLevel: 'WCAG 2.1 AA',
    lighthousePerformance: 98,
    lighthouseAccessibility: 100,
    hook: "A mock concept for a modern church that prioritizes the first-time visitor experience.",
    challenge: ["Outdated digital presence", "Confusing service times", "Friction in online giving"],
    solution: "A streamlined, cinematic homepage that answers visitor questions before they are asked.",
    impactMetrics: [
      { metric: 'Accessibility', result: '100/100' },
      { metric: 'Performance', result: '98+' }
    ],
    gallery: {
      images: [{ src: '/images/projects/other/church-site.webp', alt: 'Grace Community Chapel mockup' }],
      caption: 'Mockup concept for demonstration purposes.'
    },
    relatedProjects: ['baldie-con']
  },
  {
    slug: 'greenline-tree-service',
    name: 'Greenline Tree Service',
    orgType: 'small-business',
    isMock: true,
    heroImage: '/images/projects/other/tree-service.webp',
    tagline: 'High-performance lead generation for local trade services.',
    impactStatement: 'Built to convert local search traffic into service inquiries.',
    role: 'Technical Strategy',
    timeline: 'Productized',
    techStack: ['Next.js', 'SEO Optimization', 'Performance Build'],
    complianceLevel: 'Standard',
    lighthousePerformance: 100,
    lighthouseAccessibility: 95,
    hook: "A high-conversion landing page concept for local tree service providers.",
    challenge: ["Low conversion rates", "Slow mobile loading", "Weak local SEO"],
    solution: "Ultra-fast, mobile-optimized landing page with prominent CTAs and local trust signals.",
    impactMetrics: [
      { metric: 'Performance', result: '100/100' },
      { metric: 'Mobile Speed', result: 'Sub-second' }
    ],
    gallery: {
      images: [{ src: '/images/projects/other/tree-service.webp', alt: 'Greenline Tree Service mockup' }],
      caption: 'Mockup concept for demonstration purposes.'
    },
    relatedProjects: ['georgia-homeless-pets']
  },
  {
    slug: 'proclean-cleaning-co',
    name: 'ProClean Cleaning Co.',
    orgType: 'small-business',
    isMock: true,
    heroImage: '/images/projects/other/clean-service.webp',
    tagline: 'Clean, modern interface for high-end residential services.',
    impactStatement: 'Designed for effortless booking and trust-building.',
    role: 'Product Design',
    timeline: 'Productized',
    techStack: ['Next.js', 'Tailwind CSS', 'Responsive Design'],
    complianceLevel: 'Standard',
    lighthousePerformance: 96,
    lighthouseAccessibility: 98,
    hook: "A premium service-focused design for residential cleaning companies.",
    challenge: ["Cluttered booking process", "Generic aesthetic", "Poor mobile experience"],
    solution: "Minimalist, trust-focused design with an intuitive service selection and booking flow.",
    impactMetrics: [
      { metric: 'Design Score', result: 'Premium' },
      { metric: 'Accessibility', result: '98+' }
    ],
    gallery: {
      images: [{ src: '/images/projects/other/clean-service.webp', alt: 'ProClean Cleaning Co. mockup' }],
      caption: 'Mockup concept for demonstration purposes.'
    },
    relatedProjects: ['village-at-cobblestone']
  },
];

export const servicePackages: ServicePackage[] = [
  {
    id: 'seed',
    name: 'Foundation',
    outcomeDescriptor: 'A solid, accessible foundation for getting online',
    price: 'From $1,500',
    badge: 'Foundation',
    highlighted: false,
    features: [
      'Strategy session and site planning',
      'Tailored design aligned to your brand',
      'Content provided by client',
      'Responsive across all devices',
      'WCAG 2.1 AA accessibility',
      'On-page SEO setup (titles, meta, structure)',
      'Contact form setup',
      'Performance-optimized build',
      '2 revision rounds',
      'Launch support and handoff documentation',
      '30-day post-launch care',
    ],
    timeline: '2–3 weeks',
    revisions: '2 rounds',
    support: '30-day post-launch',
    wcagLevel: 'WCAG 2.1 AA',
    lighthouseTarget: '90+',
    checkoutUrl: '',
  },
  {
    id: 'grow',
    name: 'Growth',
    outcomeDescriptor: 'The right infrastructure for a growing organization',
    price: 'From $3,000',
    badge: 'Best value',
    highlighted: true,
    features: [
      'Everything in Foundation, plus:',
      'Content strategy and guidance',
      'Up to 10 pages, custom designed',
      'Custom CMS setup (Sanity) for easy content updates',
      'WCAG 2.1 AA accessibility',
      'SEO-ready structure, metadata, and page hierarchy',
      'Analytics integration',
      'Reusable content sections for future scalability',
      '3 revision rounds',
      'Sanity CMS training session',
      '60-day post-launch care',
    ],
    timeline: '4–6 weeks',
    revisions: '3 rounds',
    support: '60-day post-launch',
    wcagLevel: 'WCAG 2.1 AA',
    lighthouseTarget: '95+',
    checkoutUrl: '',
  },
  {
    id: 'flourish',
    name: 'Signature',
    outcomeDescriptor: 'A full-service build for complex or compliance-critical needs',
    price: 'Starting at $6,500',
    badge: 'Full service',
    highlighted: false,
    features: [
      'Everything in Growth, plus:',
      'Comprehensive site build — scope defined during discovery',
      'Technical architecture and integration planning',
      'WCAG 2.2 AA standards',
      'Accessibility documentation for grant or board use',
      'Advanced integrations and APIs',
      'Flexible iteration cycles during build phase',
      'Optimized for top-tier Lighthouse performance',
      '90-day post-launch care',
    ],
    timeline: '8–12 weeks',
    revisions: 'Flexible',
    support: '90-day post-launch',
    wcagLevel: 'WCAG 2.2 AA',
    lighthouseTarget: 'Top-tier',
    checkoutUrl: '',
  },
];

export const carePlans: CarePlan[] = [
  {
    id: 'basic',
    name: 'Laurel Basic',
    price: 150,
    checkoutUrl: '',
    includes: ['Monthly content updates (up to 2 hours)', 'Security patches & dependency updates', 'Uptime monitoring', 'Email support'],
  },
  {
    id: 'standard',
    name: 'Laurel Standard',
    price: 300,
    checkoutUrl: '',
    includes: ['Everything in Basic', 'Monthly accessibility checks', 'Performance monitoring & optimization', 'Priority email & chat support', 'Up to 4 hours of updates'],
  },
  {
    id: 'premium',
    name: 'Laurel Premium',
    price: 500,
    checkoutUrl: '',
    includes: ['Everything in Standard', 'Quarterly accessibility review', 'Analytics reporting', 'Unlimited content updates', 'Phone support', 'Same-day response'],
  },
];

export const testimonials: Testimonial[] = [
  /* Testimonials commented out
  {
    id: '1',
    quote: "Crystal rebuilt our site weeks before a major grant application deadline. The funder specifically mentioned our website's professionalism.",
    name: 'Sarah M.',
    title: 'Executive Director',
    org: 'Metro Atlanta Food Bank',
    orgType: 'nonprofit',
  },
  {
    id: '2',
    quote: 'We went from a Facebook group and email chains to a real community hub. Our residents actually use it.',
    name: 'David K.',
    title: 'HOA Board President',
    org: 'Village at Cobblestone',
    orgType: 'hoa',
  },
  */
];

export const blogPosts: BlogPost[] = [
  {
    slug: 'ada-title-ii-deadline-extended-2027',
    title: 'ADA Title II Web Accessibility Deadline Pushed to 2027: A Reprieve, Not a Reset',
    excerpt: 'On April 20, 2026, the DOJ extended Title II compliance dates by one year. Here is what actually changed, what did not, and what your organization should do with the runway.',
    category: 'Accessibility',
    readTime: 7,
    publishDate: '2026-05-01',
    featured: true,
    featuredImage: '/images/articles/ada-title-ii-deadline-extended-2027.png',
    imageAlt: 'Hand-drawn line art of a calendar with a date being moved to a later page beside a small government building and laurel sprig, with green and gold pencil hatching'
  },
  {
    slug: 'church-website-essentials',
    title: '5 Things Every Church Website Needs (And Most Are Missing)',
    excerpt: 'Your church website is often the first impression for visitors. These five elements build trust and welcome everyone, including those using assistive technology.',
    category: 'Strategy',
    readTime: 4,
    publishDate: '2026-05-01',
    featured: true,
    featuredImage: '/images/articles/church-website-essentials.png',
    imageAlt: 'Hand-drawn line art of an arched church doorway with a welcome mat and a smartphone showing a plan-your-visit list, with green and gold pencil hatching'
  },
  {
    slug: 'nonprofit-website-grant-funding',
    title: 'Does Your Nonprofit Website Qualify for Grant Funding?',
    excerpt: 'Many grant applications now require a professional, accessible web presence. Here\'s what funders are looking for along with factors that might disqualify your application.',
    category: 'Funding',
    readTime: 5,
    publishDate: '2026-05-01',
    featured: true,
    featuredImage: '/images/articles/nonprofit-website-grant-funding.png',
    imageAlt: 'Hand-drawn line art of a grant application folder with a wax-seal ribbon next to a laptop showing a website wireframe being examined by a magnifying glass, with green and gold pencil hatching'
  },
  {
    slug: 'website-maintenance-plan-nonprofit',
    title: 'Why Your Website Needs a Maintenance Plan (Not Just a Launch Plan)',
    excerpt: 'Launch day isn\'t the finish line. Learn why your nonprofit, church, or HOA website needs an ongoing maintenance plan to stay secure, fast, and trustworthy.',
    category: 'Maintenance',
    readTime: 6,
    publishDate: '2026-04-10',
    featured: true,
    featuredImage: '/images/articles/website-maintenance-plan-nonprofit.png',
    imageAlt: 'Hand-drawn line art of a laptop with a stethoscope draped across the screen beside a small wall calendar, with green and gold pencil hatching'
  },
];

export const servicesFAQ: FAQItem[] = [
  { question: 'Do I need technical knowledge to work with you?', answer: 'Not at all. We handle all the technical work and explain everything in plain language. After launch, we provide documentation and training so your team can manage content updates confidently.' },
  { question: 'Will this website help us qualify for grants?', answer: 'Many grant applications require a professional web presence. Our sites are built to meet the digital credibility standards that funders look for, including accessibility compliance, professional design, and clear organizational messaging.' },
  { question: 'What does WCAG 2.1 AA actually mean for my organization?', answer: 'WCAG 2.1 AA is a set of international accessibility guidelines. Meeting this standard means your website works for people with visual, hearing, motor, and cognitive disabilities, and it is increasingly required for government and grant-funded organizations.' },
  { question: 'Can our staff update the site after you build it?', answer: 'Absolutely. We can integrate a content management system (CMS) like Sanity that lets non-technical team members update text, images, and pages without touching code.' },
  { question: 'How long does a typical project take?', answer: 'Most projects launch in 2–8 weeks depending on scope. Foundation projects typically launch in 2–3 weeks, while Signature builds may take 8–12 weeks.' },
  { question: 'What happens after the site launches?', answer: 'Every package includes post-launch support (30–90 days depending on your plan). After that, our Care Plans provide ongoing maintenance, updates, and accessibility monitoring.' },
  { question: 'Do you work with organizations outside of Georgia?', answer: 'Yes! While we\'re based in Woodstock, Georgia, we serve organizations across the country. All of our work is done remotely using modern collaboration tools.' },
  { question: "What's included in a care plan?", answer: 'Care plans are tailored to your site\'s specific needs and can include ongoing maintenance like content updates, security patches, performance monitoring, and accessibility checks. Plans start at $75/month.' },
  { question: 'Can you help if we already have a website?', answer: 'Yes. We offer accessibility audits, performance optimization, and full redesigns. We\'ll assess your current site and recommend the most cost-effective path forward.' },
  { question: 'How do we get started?', answer: 'Book a free 30-minute consultation. We\'ll discuss your organization\'s needs, timeline, and budget, with no pitch and no pressure. From there, we\'ll send a detailed proposal within 48 hours.' },
];

export const templates = [
  {
    slug: 'nonprofit-starter',
    name: 'Nonprofit Starter',
    niche: 'nonprofit' as const,
    price: 247,
    checkoutUrl: 'https://laurelwebco.lemonsqueezy.com/checkout/buy/0cf0123f-2863-4ac0-9d48-8f6837421334?embed=1',
    previewUrl: '',
    features: ['Donation CTA', 'Grant-ready layout', 'Events section', 'WCAG 2.1 AA'],
    isNew: true,
  },
  {
    slug: 'church-essential',
    name: 'Church Essential',
    niche: 'church' as const,
    price: 247,
    checkoutUrl: 'https://laurelwebco.lemonsqueezy.com/checkout/buy/f9760583-0f69-4831-8b89-2152a73e1200?embed=1',
    previewUrl: '',
    features: ['Events calendar', 'Giving integration', 'Staff directory', 'WCAG 2.1 AA'],
    isNew: true,
  },
  {
    slug: 'hoa-hub',
    name: 'HOA Hub',
    niche: 'hoa' as const,
    price: 197,
    checkoutUrl: 'https://laurelwebco.lemonsqueezy.com/checkout/buy/42e8ae0f-ca5e-4d0b-8190-c5e4050b886a?embed=1',
    previewUrl: '',
    features: ['Resident resources', 'Vendor directory', 'Announcement board', 'CMS-ready'],
    isNew: false,
  },
];
